import React, { useState } from 'react';
import { CalendarClock, X } from 'lucide-react';
import { useAfastarContato } from '../hooks';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import type { Contato } from '../api';

interface Props {
  clientId: string;
  contact: Contato;
  candidates: Contato[];
  onClose: () => void;
  onDone?: () => void;
}

/**
 * Modal de registro de afastamento temporário (SPEC 03 Fase B —
 * Exemplo 4). Requer substituto (outro contato ativo do mesmo
 * cliente) + intervalo de datas + motivo opcional.
 *
 * Constraint EXCLUDE no banco previne sobreposição — se o usuário
 * tentar criar sobre um intervalo já ativo, o backend retorna 400
 * `CONTACT_ABSENCE_OVERLAP` e a mensagem é renderizada aqui.
 */
export const AfastamentoContatoModal: React.FC<Props> = ({
  clientId,
  contact,
  candidates,
  onClose,
  onDone,
}) => {
  const afastar = useAfastarContato(clientId);
  const [substituteContactId, setSubstituteContactId] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const podeSubmeter = !!substituteContactId && !!startsAt && !!endsAt && startsAt <= endsAt;

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    setError(null);
    if (!substituteContactId) {
      setError('Selecione o substituto.');
      return;
    }
    if (!startsAt || !endsAt) {
      setError('Informe as datas de início e fim.');
      return;
    }
    if (startsAt > endsAt) {
      setError('Data de início deve ser <= data de fim.');
      return;
    }
    try {
      await afastar.mutateAsync({
        contactId: contact.id,
        substituteContactId,
        startsAt,
        endsAt,
        reason: reason.trim() || undefined,
      });
      onDone?.();
      onClose();
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Erro ao registrar afastamento.'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarClock size={20} /> Registrar afastamento
          </h3>
          <button type="button" onClick={onClose} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-6" data-testid="afastamento-form">
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="text-xs uppercase tracking-wide text-gray-500">Contato afastado</div>
            <div className="font-medium text-gray-900">{contact.nome}</div>
          </div>

          <AutocompleteSelect
            label="Substituto *"
            options={[
              { value: '', label: 'Selecione…' },
              ...candidates.map((c) => ({ value: c.id, label: `${c.nome} — ${c.email}` })),
            ]}
            value={substituteContactId}
            onChange={(v) => setSubstituteContactId(v)}
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <TextInput
              label="Início *"
              type="date"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              data-testid="afastamento-inicio"
            />
            <TextInput
              label="Fim *"
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              data-testid="afastamento-fim"
            />
          </div>

          <div>
            <Textarea
              label="Motivo"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="afastamento-motivo"
            />
            <span className="mt-1 block text-xs text-gray-500">
              Durante o intervalo, notificações e sugestões automáticas são direcionadas ao
              substituto. Ao fim da data, retorna automaticamente ao titular.
            </span>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid="afastamento-error">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Cancelar
            </button>
            <button
              type="submit"
              disabled={afastar.isPending || !podeSubmeter}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              data-testid="afastamento-btn-confirmar"
            >
              {afastar.isPending ? 'Registrando…' : 'Registrar afastamento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
